import React from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd/lib/table';
import { showTotalRecords } from '@app/utils/tablePagination';
import { formatNumberWithCommas } from '@app/utils/utils';
import styles from './BaseTable.module.css';

/**
 * Table dùng chung, luôn cho người dùng thấy tổng số bản ghi.
 *
 * - Bảng có phân trang: bổ sung `showTotal` nếu nơi gọi chưa tự đặt.
 * - Bảng tắt phân trang (`pagination={false}` — bảng dòng hàng trong form,
 *   bảng chi tiết báo cáo): thêm một dòng "Tổng: N bản ghi" bên dưới, vì tắt
 *   phân trang đồng nghĩa không còn chỗ nào hiện số lượng.
 *
 * Mọi prop khác giữ nguyên hành vi của antd Table.
 *
 * Mặc định `scroll.x: 'max-content'`: khung layout đặt `overflow-x: hidden`
 * toàn trang, nên bảng nhiều cột không có scroll.x sẽ bị CẮT cột (không cuộn
 * tới được) trên màn hẹp. Nơi gọi truyền `scroll` riêng thì giữ nguyên.
 */
function BaseTable<RecordType extends object = any>(props: TableProps<RecordType>): React.ReactElement {
  const { pagination, dataSource, scroll, ...rest } = props;

  const mergedScroll = { x: 'max-content' as const, ...(scroll || {}) };

  if (pagination === false) {
    const total = dataSource?.length ?? 0;

    return (
      <>
        <Table<RecordType> {...rest} scroll={mergedScroll} dataSource={dataSource} pagination={false} />
        <div className={styles.totalRow}>
          {total > 0 ? (
            <>
              Tổng: <strong>{formatNumberWithCommas(total)}</strong> bản ghi
            </>
          ) : (
            'Không có bản ghi nào'
          )}
        </div>
      </>
    );
  }

  return (
    <Table<RecordType>
      {...rest}
      scroll={mergedScroll}
      dataSource={dataSource}
      pagination={{ showTotal: showTotalRecords, ...(pagination || {}) }}
    />
  );
}

export default BaseTable;
